import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit2, Trash2, X, ChevronUp, ChevronDown } from 'lucide-react'
import api from '../services/api'
import Card from '../components/Card'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken, clearAuth } from '../utils/userData'

const INITIAL_FORM = {
  caption: '',
  duration: '',
}

const FacilitatorGalleryManage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [mediaFile, setMediaFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [reordering, setReordering] = useState(false)

  useEffect(() => {
    const token = getToken()
    if (!token) {
      navigate('/')
      return
    }
    fetchItems()
  }, [navigate])

  const fetchItems = async () => {
    try {
      setLoading(true)
      const response = await api.get('/api/facilitators/gallery')
      if (response.data.success) {
        setItems(response.data.data)
      }
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuth()
        navigate('/')
      }
      console.error('Error fetching gallery:', err)
    } finally {
      setLoading(false)
    }
  }

  const openCreateForm = () => {
    setForm(INITIAL_FORM)
    setMediaFile(null)
    setEditingItem(null)
    setShowForm(true)
  }

  const openEditForm = (item) => {
    setForm({
      caption: item.caption || '',
      duration: item.duration || '',
    })
    setMediaFile(null)
    setEditingItem(item)
    setShowForm(true)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    try {
      setSaving(true)
      if (editingItem) {
        await api.put(`/api/facilitators/gallery/${editingItem.id}`, {
          caption: form.caption,
          duration: form.duration,
        })
      } else {
        if (!mediaFile) return
        const formData = new FormData()
        formData.append('file', mediaFile)
        if (form.caption) formData.append('caption', form.caption)
        if (form.duration) formData.append('duration', form.duration)
        await api.post('/api/facilitators/gallery', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
      setShowForm(false)
      fetchItems()
    } catch (err) {
      console.error('Error saving gallery item:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gallery item? This cannot be undone.')) return
    try {
      await api.delete(`/api/facilitators/gallery/${id}`)
      fetchItems()
    } catch (err) {
      console.error('Error deleting gallery item:', err)
    }
  }

  const moveItem = async (index, direction) => {
    const newItems = [...items]
    const swapIndex = index + direction
    if (swapIndex < 0 || swapIndex >= newItems.length) return
    ;[newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]]
    setItems(newItems)
    try {
      setReordering(true)
      await api.put('/api/facilitators/gallery/reorder', {
        orderedIds: newItems.map(item => item.id),
      })
    } catch (err) {
      console.error('Error reordering gallery:', err)
      fetchItems()
    } finally {
      setReordering(false)
    }
  }

  const isVideo = (item) =>
    item.mediaType === 'video' ||
    (item.fileUrl && /\.(mp4|webm|ogg|mov)$/i.test(item.fileUrl))

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-cream to-primary-100">
      <div className="container mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">My Gallery</h1>
              <p className="text-gray-600">Manage the photos and videos shown on your profile.</p>
            </div>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Media
            </button>
          </div>
        </motion.div>

        {items.length === 0 ? (
          <Card className="text-center py-16">
            <p className="text-gray-500 text-lg mb-4">Your gallery is empty.</p>
            <button
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Your First Media
            </button>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {items.map((item, index) => (
              <motion.div key={item.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
                <Card className="overflow-hidden group">
                  <div className="relative aspect-square bg-gray-100">
                    {isVideo(item) ? (
                      <video src={item.fileUrl} className="w-full h-full object-cover" muted />
                    ) : (
                      <img
                        src={item.fileUrl}
                        alt={item.caption || 'Gallery item'}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {isVideo(item) && (
                      <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
                        Video
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => openEditForm(item)}
                        className="p-2 bg-white rounded-full text-gray-700 hover:text-blue-600 transition-colors"
                        title="Edit caption"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 bg-white rounded-full text-gray-700 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {item.caption && (
                    <div className="px-3 py-2">
                      <p className="text-xs text-gray-600 line-clamp-2">{item.caption}</p>
                    </div>
                  )}
                  <div className="flex justify-center gap-1 px-2 pb-2">
                    <button
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0 || reordering}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors"
                      title="Move left"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveItem(index, 1)}
                      disabled={index === items.length - 1 || reordering}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors"
                      title="Move right"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
              onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false) }}
            >
              <motion.div
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '100%', opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto"
              >
                <form onSubmit={handleSave} className="p-6 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingItem ? 'Edit Caption' : 'Add Media'}
                    </h2>
                    <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-full">
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {!editingItem && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        File <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        required
                        onChange={(e) => setMediaFile(e.target.files[0] || null)}
                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                      />
                    </div>
                  )}

                  {((!editingItem && mediaFile?.type?.startsWith('video')) ||
                    (editingItem && isVideo(editingItem))) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                      <input
                        type="text"
                        value={form.duration}
                        onChange={(e) => setForm(prev => ({ ...prev, duration: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="e.g., 2:30"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                    <input
                      type="text"
                      value={form.caption}
                      onChange={(e) => setForm(prev => ({ ...prev, caption: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Optional caption..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={saving || (!editingItem && !mediaFile)}
                    className="w-full py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Uploading...' : editingItem ? 'Update Caption' : 'Upload'}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default FacilitatorGalleryManage
